import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "@/lib/api";
import {
  ShieldCheck, Pulse, CheckCircle, WarningCircle,
  Cloud, Database, EnvelopeSimple, Robot, Clock, MapPin, ArrowsClockwise,
} from "@phosphor-icons/react";

const COMPONENT_ICON = {
  API: Cloud,
  Database: Database,
  "Email delivery": EnvelopeSimple,
  "AI compliance advisor": Robot,
  "Weekly digest scheduler": Clock,
};

const STATE_COLOR = {
  operational: { fg: "#00A86B", bg: "#00A86B22", label: "Operational" },
  degraded: { fg: "#FFB020", bg: "#FFB02022", label: "Degraded" },
  "not configured": { fg: "#9BA3A0", bg: "#9BA3A022", label: "Not configured" },
  down: { fg: "#E63946", bg: "#E6394622", label: "Down" },
};

const fmtUptime = (seconds) => {
  if (!seconds) return "0s";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${seconds}s`;
};

const fmtEndpoint = (path) =>
  path.replace("/api/", "").replace(/^\/+/, "");

export default function Status() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/public/status`);
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    document.title = "System Status · PerCen360";
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, []);

  if (loading) return (
    <div className="min-h-screen aurora flex items-center justify-center">
      <div className="glass rounded-2xl px-10 py-8 text-center">
        <div className="eyebrow mb-3">Loading status…</div>
      </div>
    </div>
  );

  const isOk = data?.status === "operational";
  const heroColor = isOk ? "#00A86B" : "#FFB020";

  return (
    <div className="min-h-screen aurora grain" data-testid="status-page">
      {/* Top bar */}
      <div className="border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#FFB020" }}>
              <ShieldCheck size={22} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div className="font-display leading-none">PerCen360</div>
              <div className="text-[9px] tracking-[0.24em] uppercase text-white/45 mt-1">System Status</div>
            </div>
          </Link>
          <button
            onClick={load}
            disabled={refreshing}
            className="btn-ghost text-xs"
            data-testid="btn-refresh-status"
          >
            <ArrowsClockwise size={13} weight="bold" className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-14 space-y-12">
        {/* Hero indicator */}
        <motion.section
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="glass rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden"
          data-testid="status-hero"
        >
          <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 50% 0%, ${heroColor}, transparent 60%)` }} />
          <div className="relative">
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="relative flex h-3 w-3" data-testid="status-pulse">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: heroColor }}></span>
                <span className="relative inline-flex rounded-full h-3 w-3" style={{ background: heroColor }}></span>
              </span>
              <span className="eyebrow text-[10px]" style={{ color: heroColor }}>Live · {data.region}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="status-title">
              {isOk ? "All systems operational." : "Some systems degraded."}
            </h1>
            <p className="text-white/60 text-sm mt-4 max-w-xl mx-auto">
              Real-time transparency for grant bodies, investors and auditors. GDPR-safe: aggregate metrics only, no PII.
            </p>
            {error && <div className="text-[#E63946] text-xs mt-4">{error}</div>}
          </div>
        </motion.section>

        {/* Component grid */}
        <section className="space-y-4">
          <div className="eyebrow flex items-center gap-2">
            <Pulse size={12} weight="fill" color="#FFB020" /> Components
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="components-grid">
            {data.components.map((c, i) => {
              const Icon = COMPONENT_ICON[c.name] || CheckCircle;
              const s = STATE_COLOR[c.state] || STATE_COLOR.operational;
              return (
                <motion.div
                  key={c.name}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  className="glass rounded-xl p-5 flex items-center justify-between"
                  data-testid={`component-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center border border-white/10 bg-black/30">
                      <Icon size={18} weight="duotone" color={s.fg} />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white/90">{c.name}</div>
                      <div className="text-[11px] text-white/45 mt-0.5">{c.note}</div>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[11px] font-semibold" style={{ background: s.bg, color: s.fg }}>
                    {s.label}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Endpoint latency table */}
        <section className="glass rounded-2xl p-6 sm:p-8" data-testid="endpoints-section">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="eyebrow flex items-center gap-2">
              <Pulse size={12} weight="fill" color="#7AE1B3" /> Endpoint latency · last 24h
            </div>
            <div className="text-[11px] text-white/45 mono">
              {data.traffic_24h.requests.toLocaleString()} total requests tracked
            </div>
          </div>
          <table className="data" data-testid="endpoints-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th className="text-right">Requests</th>
                <th className="text-right">p50</th>
                <th className="text-right">p95</th>
                <th className="text-right">Errors</th>
              </tr>
            </thead>
            <tbody>
              {data.endpoints_24h.map((e) => (
                <tr key={e.endpoint} data-testid={`endpoint-${e.endpoint.replace(/[^a-z0-9]+/gi, "-")}`}>
                  <td className="mono text-xs">{fmtEndpoint(e.endpoint)}</td>
                  <td className="text-right mono text-xs text-white/70">{e.requests_24h.toLocaleString()}</td>
                  <td className="text-right mono text-xs">{e.p50_ms}<span className="text-white/40">ms</span></td>
                  <td className="text-right mono text-xs">{e.p95_ms}<span className="text-white/40">ms</span></td>
                  <td className="text-right">
                    <span className={`mono text-xs ${e.error_rate_pct > 0 ? "text-[#E63946]" : "text-[#00A86B]"}`}>
                      {e.error_rate_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.endpoints_24h.every((e) => e.requests_24h === 0) && (
            <div className="text-center text-white/45 text-xs mt-6">
              No public traffic in the last 24h — data will populate as requests arrive.
            </div>
          )}
        </section>

        {/* Meta row */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="glass rounded-xl p-5" data-testid="meta-uptime">
            <div className="eyebrow text-[10px]">Uptime</div>
            <div className="mono text-2xl mt-3">{fmtUptime(data.uptime_seconds)}</div>
            <div className="text-[11px] text-white/45 mt-1">since {data.up_since?.slice(0, 19).replace("T", " ")} UTC</div>
          </div>
          <div className="glass rounded-xl p-5" data-testid="meta-region">
            <div className="eyebrow text-[10px] flex items-center gap-1"><MapPin size={11}/> Region</div>
            <div className="mono text-2xl mt-3">EU · Ireland</div>
            <div className="text-[11px] text-white/45 mt-1">EU region</div>
          </div>
          <div className="glass rounded-xl p-5" data-testid="meta-scheduler">
            <div className="eyebrow text-[10px]">Weekly digest cron</div>
            <div className="mono text-sm mt-3 text-white/85">
              {data.scheduler.last_digest_run_at
                ? data.scheduler.last_digest_run_at.slice(0, 19).replace("T", " ")
                : "Not run since restart"}
            </div>
            <div className="text-[11px] text-white/45 mt-1">Sunday 07:00 UTC</div>
          </div>
        </section>

        {/* GDPR note */}
        <section className="text-center pt-4">
          <div className="inline-flex items-center gap-2 text-xs text-white/50 px-4 py-2 rounded-full border border-white/10 bg-black/20">
            <ShieldCheck size={13} weight="duotone" color="#7AE1B3" />
            {data.gdpr_note}
          </div>
        </section>

        {/* Footer CTA */}
        <section className="text-center py-6">
          <Link to="/" className="btn-ghost text-xs">← Back to PerCen360</Link>
        </section>
      </div>
    </div>
  );
}
